import { todayStr, toLocalDateStr } from "../lib/date";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  getStudents, createBatchAssessments, getAssessments,
  getCustomGroups, createCustomGroup, updateCustomGroup, deleteCustomGroup,
  getAttendance, getClassGroups, updateAssessment, deleteAssessment,
  getCoreClasses, getMasterScheduleOverlay, getAssessmentRubric, getMasterScheduleHighlightGroups, getMasterScheduleDayClasses, getUnscheduledBlocks
} from "../lib/api";
import { useAuth } from "../App";
import { useSearchParams } from "react-router-dom";
import { firstName } from "../lib/names";
import { StudentAssessmentCard } from "../components/assessment/StudentAssessmentCard";
import { CustomGroupDialog } from "../components/assessment/CustomGroupDialog";
import { AssessmentEntryRow } from "../components/assessment/AssessmentEntryRow";
import DnaReviewDialog from "../components/assessment/DnaReviewDialog";

const BLOCKS = ["Block 1", "Block 2", "Block 3", "Block 4", "Block 5", "Block 6"];
const FULL_DAY = "Full Day Outing";  // internal sentinel for trip mode (kept for saved-data continuity)
const TRIP_LABEL = "Trip";
const blockNameFromKey = (k) => { const m = (k || "").match(/(\d+)/); return m ? `Block ${m[1]}` : (k || ""); };

// A sign-up slot "belongs to" the assessment block when their names match
// (e.g. slot "Block 5" ↔ block "Block 5"), or the slot name mentions the block's number.
const slotMatchesBlock = (slotName, block) => {
  const norm = (s) => (s || "").toLowerCase().replace(/[^a-z0-9]/g, "");
  if (!slotName || !block) return false;
  if (norm(slotName) === norm(block)) return true;
  const num = (block.match(/\d+/) || [])[0];
  return !!num && new RegExp(`(^|[^0-9])${num}([^0-9]|$)`).test(slotName);
};

const Icon = ({ d, size = 20, className = "" }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}>{d}</svg>
);
const ChevronLeft = (p) => <Icon {...p} d={<path d="m15 18-6-6 6-6" />} />;
const ChevronRight = (p) => <Icon {...p} d={<path d="m9 18 6-6-6-6" />} />;
const SunIcon = (p) => <Icon {...p} d={<><circle cx="12" cy="12" r="4" /><path d="M12 2v2M12 20v2M4.9 4.9l1.4 1.4M17.7 17.7l1.4 1.4M2 12h2M20 12h2M4.9 19.1l1.4-1.4M17.7 6.3l1.4-1.4" /></>} />;
const UsersIcon = (p) => <Icon {...p} d={<><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" /></>} />;
const GridIcon = (p) => <Icon {...p} d={<><rect x="3" y="3" width="7" height="7" /><rect x="14" y="3" width="7" height="7" /><rect x="14" y="14" width="7" height="7" /><rect x="3" y="14" width="7" height="7" /></>} />;
const UserIcon = (p) => <Icon {...p} d={<><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>} />;

export default function AssessmentPage() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const deepLinkStudent = searchParams.get("student");
  const [students, setStudents] = useState([]);
  const [customGroups, setCustomGroups] = useState([]);
  const [assessments, setAssessments] = useState([]);
  const [presentStudentIds, setPresentStudentIds] = useState(null);
  // Students explicitly marked absent / on pause / planned schedule for the day.
  // Always hidden from the score list (even before anyone is marked present) so a
  // teacher can never accidentally record a DNA for a student who wasn't here.
  const [absentStudentIds, setAbsentStudentIds] = useState(() => new Set());
  const [unscheduledMap, setUnscheduledMap] = useState({}); // {student_id: [block names]} not scheduled per alt plan
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => searchParams.get("date") || todayStr());
  const [selectedGroup, setSelectedGroup] = useState(() => deepLinkStudent ? "one_on_one" : "morning_1");
  const [className, setClassName] = useState("");
  const [classFromParam] = useState(() => searchParams.get("class_name") || "");
  const [selectedBlock, setSelectedBlock] = useState(() => searchParams.get("block") || "Block 1");
  const [oneOnOneStudent, setOneOnOneStudent] = useState(() => deepLinkStudent || "");
  const [studentAssessments, setStudentAssessments] = useState({});
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [groupDialogOpen, setGroupDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [dnaReviewOpen, setDnaReviewOpen] = useState(false);
  const [classGroupConfig, setClassGroupConfig] = useState({ morning_groups: [], afternoon_groups: [] });
  const [subjectOptions, setSubjectOptions] = useState([]);
  const [otherClassName, setOtherClassName] = useState("");
  const [scheduleClasses, setScheduleClasses] = useState([]);        // master-schedule class names for selectedDate (1:1s excluded)
  const [scheduleClassChoice, setScheduleClassChoice] = useState(""); // secondary picker when primary = "Other" ("__other__" = free-type)
  const [fullDayTrips, setFullDayTrips] = useState([]);
  const [selectedTripId, setSelectedTripId] = useState("");
  const [fullDayStudentPool, setFullDayStudentPool] = useState([]);
  const [blockSignups, setBlockSignups] = useState([]);      // sign-up options for the selected block+date
  const [blockHighlightGroups, setBlockHighlightGroups] = useState([]); // MS highlight/small-group entries for the block+date
  const [signupPool, setSignupPool] = useState([]);          // full current roster for resolving sign-up rosters
  const [rubricImage, setRubricImage] = useState("");
  // Wizard: block -> class -> group -> score (deep-links jump straight to the score sheet)
  const [step, setStep] = useState(() => deepLinkStudent ? "score" : "block");

  // Full Day Outing mode: one assessment covers the whole day; roster = the picked trip's sign-ups.
  const isFullDay = selectedBlock === FULL_DAY;
  const selectedTrip = fullDayTrips.find((t) => t.id === selectedTripId) || null;
  const fullDayStudentIds = selectedTrip ? (selectedTrip.student_ids || []) : [];
  // Trip covers specific blocks — the assessment is scoped/labeled to those blocks.
  const tripCoveredBlocks = (selectedTrip && (selectedTrip.covered_blocks || [])) || [];
  const tripBlockLabel = tripCoveredBlocks.length ? tripCoveredBlocks.map(blockNameFromKey).join(", ") : "All blocks";
  // Sign-up group mode: a sign-up activity's roster becomes a ready-made assessment group.
  const isSignup = selectedGroup.startsWith("signup_");
  const selectedSignup = isSignup ? blockSignups.find((o) => `signup_${o.id}` === selectedGroup) || null : null;
  // Master-Schedule highlight/small-group mode: a "Highlight (1:1) + Individuals" cell
  // becomes a ready-made group. The subject stays what the staff entered (NOT the MS subject).
  const isMsGroup = selectedGroup.startsWith("msg_");
  const selectedMsGroup = isMsGroup ? blockHighlightGroups.find((g) => `msg_${g.gid}` === selectedGroup) || null : null;
  const effGroup = isFullDay ? "full_day_outing"
    : isSignup ? "signup"
    : isMsGroup ? ((selectedMsGroup && (selectedMsGroup.student_ids || []).length === 1) ? "one_on_one" : "highlight")
    : selectedGroup;
  // When primary class = "Other", the real class name comes from the secondary
  // schedule picker (for name uniformity) or, if "Other" is chosen there too, free-text.
  const resolvedOtherClass = scheduleClassChoice && scheduleClassChoice !== "__other__"
    ? scheduleClassChoice
    : (otherClassName.trim() || "Other");
  const classChosen = className === "Other" ? resolvedOtherClass : className;
  const effClass = isFullDay
    ? (selectedTrip ? selectedTrip.title : FULL_DAY)
    : isSignup ? (selectedSignup ? selectedSignup.title : "Sign-Up") : classChosen;
  // Saved block: for a trip, the specific blocks it covers (so reports show the span).
  const effBlock = isFullDay
    ? (tripCoveredBlocks.length ? tripCoveredBlocks.map(blockNameFromKey).join(", ") : "Trip")
    : selectedBlock;

  // Students hidden for the SELECTED block because an active alternate plan means
  // they aren't in the building for it (late arrival / early departure / full-day-out).
  // Keeps teachers from marking an illegitimate DNA — JJ (arrives 10:30) only shows
  // from the block he's actually present for.
  const blockUnscheduledIds = (() => {
    if (isFullDay) return null;
    const set = new Set();
    Object.entries(unscheduledMap || {}).forEach(([sid, blocks]) => {
      if ((blocks || []).includes(selectedBlock)) set.add(sid);
    });
    return set;
  })();

  const getActiveStudents = () => {
    if (isFullDay) {
      return fullDayStudentPool.filter((s) => fullDayStudentIds.includes(s.id));
    }
    const list = getStudentsByGroup(selectedGroup);
    // Don't filter an explicit 1:1 pick (teacher chose the student deliberately).
    if (selectedGroup === "one_on_one") return list;
    if (blockUnscheduledIds && blockUnscheduledIds.size) {
      return list.filter((s) => !blockUnscheduledIds.has(s.id));
    }
    return list;
  };

  const fetchData = async () => {
    try {
      const [studentsRes, assessmentsRes, groupsRes, attendanceRes, classGroupsRes, coreClassesRes] = await Promise.all([
        getStudents("current"),
        getAssessments({ date: selectedDate }),
        getCustomGroups(),
        getAttendance({ date: selectedDate }),
        getClassGroups(),
        getCoreClasses(),
      ]);
      getUnscheduledBlocks(selectedDate).then(r => setUnscheduledMap(r.data || {})).catch(() => setUnscheduledMap({}));
      setStudents(studentsRes.data);
      setAssessments(assessmentsRes.data);
      setCustomGroups([...(groupsRes.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || "")));
      setClassGroupConfig(classGroupsRes.data);
      const coreList = Array.isArray(coreClassesRes.data) ? coreClassesRes.data : [];
      const withOther = coreList.includes("Other") ? coreList : [...coreList, "Other"];
      setSubjectOptions(withOther);
      if (classFromParam && withOther.includes(classFromParam)) {
        setClassName(classFromParam);
      } else if (classFromParam && !withOther.includes(classFromParam)) {
        setClassName("Other");
        setScheduleClassChoice("__other__");
        setOtherClassName(classFromParam);
      } else {
        setClassName(prev => withOther.includes(prev) ? prev : (withOther[0] || ""));
      }

      const present = new Set();
      const absent = new Set();
      const ABSENT_STATUSES = new Set(["absent_excused", "absent_unexcused", "absent", "excused", "unexcused", "on_pause", "planned_schedule"]);
      for (const record of attendanceRes.data) {
        if (ABSENT_STATUSES.has(record.status)) {
          absent.add(record.student_id);
        } else if (record.check_in_time || record.status === "present" || record.status === "tardy" || record.status === "left_early_unexcused") {
          present.add(record.student_id);
        }
      }
      setPresentStudentIds(present.size > 0 ? present : null);
      setAbsentStudentIds(absent);
    } catch (err) {
      toast.error("Failed to load assessment data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [selectedDate]);

  // Day-specific master-schedule class names (1:1s excluded) for the "Other" picker.
  useEffect(() => {
    getMasterScheduleDayClasses(selectedDate)
      .then((r) => setScheduleClasses(Array.isArray(r.data) ? r.data : []))
      .catch(() => setScheduleClasses([]));
  }, [selectedDate]);

  useEffect(() => {
    getAssessmentRubric().then((r) => setRubricImage(r.data?.image || "")).catch(() => {});
  }, []);

  const mondayOfDate = (iso) => {
    const d = new Date(iso + "T00:00:00");
    const day = d.getDay();
    d.setDate(d.getDate() + ((day === 0 ? -6 : 1) - day));
    return toLocalDateStr(d);
  };
  useEffect(() => {
    if (!isFullDay) { setFullDayTrips([]); setSelectedTripId(""); return; }
    (async () => {
      try {
        const [overlayRes, poolRes] = await Promise.all([
          getMasterScheduleOverlay(mondayOfDate(selectedDate)),
          getStudents("current", true),
        ]);
        const trips = [...(overlayRes.data?.by_date?.[selectedDate]?.trips || [])].sort((a, b) => (a.title || "").localeCompare(b.title || ""));
        setFullDayTrips(trips);
        setSelectedTripId((prev) => (trips.some((t) => t.id === prev) ? prev : (trips[0]?.id || "")));
        setFullDayStudentPool(poolRes.data || []);
      } catch { setFullDayTrips([]); setSelectedTripId(""); setFullDayStudentPool([]); }
    })();
  }, [isFullDay, selectedDate]);

  // Load sign-up activities + Master-Schedule highlight/small-group entries tied to the
  // selected block + date (both become ready-made assessment groups).
  useEffect(() => {
    if (isFullDay || !selectedBlock) { setBlockSignups([]); setBlockHighlightGroups([]); return; }
    const blockKey = selectedBlock.toLowerCase().replace(/\s/g, "");
    (async () => {
      try {
        const [overlayRes, poolRes, hlRes] = await Promise.all([
          getMasterScheduleOverlay(mondayOfDate(selectedDate)),
          getStudents("current", true),
          getMasterScheduleHighlightGroups(selectedDate).catch(() => ({ data: { groups: [] } })),
        ]);
        const slots = overlayRes.data?.by_date?.[selectedDate]?.slots || {};
        const opts = [];
        Object.values(slots).forEach((slot) => {
          if (slotMatchesBlock(slot.slot_name, selectedBlock)) {
            (slot.options || []).forEach((o) => opts.push({ ...o, slot_name: slot.slot_name }));
          }
        });
        setBlockSignups(opts);
        setSignupPool(poolRes.data || []);
        const hls = (hlRes.data?.groups || [])
          .filter((g) => g.block_key === blockKey)
          .map((g, idx) => ({ ...g, gid: `${g.block_key}_${idx}` }));
        setBlockHighlightGroups(hls);
      } catch { setBlockSignups([]); setBlockHighlightGroups([]); }
    })();
  }, [isFullDay, selectedBlock, selectedDate]);

  useEffect(() => {
    const groupStudentsList = getActiveStudents();
    const newAssessments = {};
    groupStudentsList.forEach(student => {
      const existingAssessment = assessments.find(
        a => a.student_id === student.id && a.group_type === effGroup && a.class_name === effClass && a.block === effBlock
      );
      if (existingAssessment) {
        newAssessments[student.id] = {
          did_not_attend: existingAssessment.did_not_attend || false,
          participation_level: existingAssessment.participation_level,
          engagement: existingAssessment.engagement,
          communication: existingAssessment.communication,
          behavior: existingAssessment.behavior,
          attention: existingAssessment.attention,
          work_completion: existingAssessment.work_completion,
          notes: existingAssessment.notes || "",
          alternate_plan: existingAssessment.alternate_plan || "",
        };
      } else {
        newAssessments[student.id] = {
          did_not_attend: false, participation_level: null, engagement: null,
          communication: null, behavior: null, attention: null, work_completion: null,
          notes: "", alternate_plan: "",
        };
      }
    });
    setStudentAssessments(newAssessments);
    setHasUnsavedChanges(false);
  }, [selectedGroup, className, selectedBlock, students, assessments, presentStudentIds, absentStudentIds, selectedTripId, fullDayTrips, fullDayStudentPool, blockSignups, blockHighlightGroups, signupPool, unscheduledMap]);

  useEffect(() => {
    if (selectedGroup === "one_on_one" && oneOnOneStudent) {
      const existing = assessments.find(
        a => a.student_id === oneOnOneStudent && a.group_type === "one_on_one" && a.class_name === className && a.block === selectedBlock
      );
      setStudentAssessments({
        [oneOnOneStudent]: existing ? {
          did_not_attend: existing.did_not_attend || false,
          participation_level: existing.participation_level,
          engagement: existing.engagement, communication: existing.communication,
          behavior: existing.behavior, attention: existing.attention,
          work_completion: existing.work_completion,
          notes: existing.notes || "", alternate_plan: existing.alternate_plan || "",
        } : {
          did_not_attend: false, participation_level: null, engagement: null,
          communication: null, behavior: null, attention: null, work_completion: null,
          notes: "", alternate_plan: "",
        }
      });
    }
  }, [oneOnOneStudent, selectedGroup, className, selectedBlock, assessments]);

  const getStudentsByGroup = (groupType) => {
    // present-only (when attendance has been taken) AND always drop absent/on-pause.
    const applyPresence = (list) => {
      let out = presentStudentIds ? list.filter(s => presentStudentIds.has(s.id)) : list;
      if (absentStudentIds.size) out = out.filter(s => !absentStudentIds.has(s.id));
      return out;
    };
    if (groupType === "one_on_one") {
      if (!oneOnOneStudent) return [];
      const s = students.find(st => st.id === oneOnOneStudent);
      return s ? [s] : [];
    }
    if (groupType.startsWith("signup_")) {
      const opt = blockSignups.find(o => `signup_${o.id}` === groupType);
      if (!opt) return [];
      const ids = opt.student_ids || [];
      const pool = signupPool.length ? signupPool : students;
      let filtered = pool.filter(s => ids.includes(s.id));
      return applyPresence(filtered);
    }
    if (groupType.startsWith("msg_")) {
      const g = blockHighlightGroups.find(x => `msg_${x.gid}` === groupType);
      if (!g) return [];
      const ids = g.student_ids || [];
      const pool = signupPool.length ? signupPool : students;
      let filtered = pool.filter(s => ids.includes(s.id));
      return applyPresence(filtered);
    }
    if (groupType.startsWith("custom_")) {
      const groupId = groupType.replace("custom_", "");
      const customGroup = customGroups.find(g => g.id === groupId);
      if (customGroup) {
        let filtered = students.filter(s => customGroup.student_ids.includes(s.id));
        return applyPresence(filtered);
      }
      return [];
    }
    if (groupType === "individual") {
      return applyPresence([...students]);
    }
    if (groupType.startsWith("morning_") || groupType.startsWith("afternoon_")) {
      const parts = groupType.split("_");
      const period = parts[0];
      const groupKey = parts.slice(1).join("_");
      const field = period === "morning" ? "morning_group" : "afternoon_group";
      let filtered = students.filter((s) => s[field] === groupKey);
      return applyPresence(filtered);
    }
    return students;
  };

  const handleStudentAssessmentChange = (studentId, assessment) => {
    setStudentAssessments(prev => ({ ...prev, [studentId]: assessment }));
    setHasUnsavedChanges(true);
  };

  const handleSubmitAll = async () => {
    const groupStudentsList = getActiveStudents();
    const assessmentsToSave = groupStudentsList
      .map(student => ({ student_id: student.id, ...studentAssessments[student.id] }))
      .filter(a => {
        if (a.did_not_attend) return true;
        return a.participation_level != null || a.engagement != null || a.communication != null ||
               a.behavior != null || a.attention != null || a.work_completion != null;
      });

    if (assessmentsToSave.length === 0) {
      toast.error("No scores to save. Enter scores for at least one student.");
      return;
    }

    const batchData = {
      date: selectedDate,
      group_type: effGroup,
      class_name: effClass,
      block: effBlock,
      teacher_id: user.id,
      assessments: assessmentsToSave,
    };
    if (isFullDay) batchData.covered_blocks = tripCoveredBlocks;

    setSubmitting(true);
    try {
      const res = await createBatchAssessments(batchData);
      toast.success(`Assessments saved! (${res.data.created} new, ${res.data.updated} updated)`);
      setHasUnsavedChanges(false);
      fetchData();
    } catch (err) {
      toast.error("Failed to save assessments");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateGroup = async (groupData) => {
    try { await createCustomGroup(groupData); toast.success("Custom group created!"); fetchData(); }
    catch { toast.error("Failed to create group"); }
  };
  const handleUpdateGroup = async (groupData) => {
    try { await updateCustomGroup(editingGroup.id, groupData); toast.success("Group updated!"); setEditingGroup(null); fetchData(); }
    catch { toast.error("Failed to update group"); }
  };
  const handleDeleteGroup = async (groupId) => {
    if (!confirm("Are you sure you want to delete this group?")) return;
    try {
      await deleteCustomGroup(groupId);
      toast.success("Group deleted");
      if (selectedGroup === `custom_${groupId}`) setSelectedGroup("morning_1");
      fetchData();
    } catch { toast.error("Failed to delete group"); }
  };

  // Group options for the "Who is this for?" step (standard AM/PM groups)
  const groupOptions = [
    ...(classGroupConfig.morning_groups || []).map(g => ({ value: `morning_${g.key}`, label: g.name, sub: "Morning group" })),
    ...(classGroupConfig.afternoon_groups || []).map(g => ({ value: `afternoon_${g.key}`, label: g.name, sub: "Afternoon group" })),
  ];

  const standardGroupTabs = [
    ...groupOptions.map(g => ({ value: g.value, label: g.label })),
    { value: "individual", label: "Individual" },
  ];

  const allStudentsSorted = [...students].sort((a, b) =>
    `${a.preferred_name || a.first_name} ${a.last_name}`.localeCompare(`${b.preferred_name || b.first_name} ${b.last_name}`)
  );

  const groupStudents = (isFullDay ? getActiveStudents() : getStudentsByGroup(selectedGroup))
    .sort((a, b) => (a.preferred_name || a.first_name).localeCompare(b.preferred_name || b.first_name));

  const allGroupKeys = [
    ...standardGroupTabs.map(t => t.value),
    ...customGroups.map(g => `custom_${g.id}`),
  ];

  const confirmGroupSwitch = (callback) => {
    if (hasUnsavedChanges) {
      if (!window.confirm("You have unsaved changes. Switch groups anyway? Your changes will be lost.")) return;
      setHasUnsavedChanges(false);
    }
    callback();
  };

  const handleQuickSwap = (direction) => {
    confirmGroupSwitch(() => {
      const currentIdx = allGroupKeys.indexOf(selectedGroup);
      if (currentIdx === -1) return;
      const nextIdx = direction === "next"
        ? (currentIdx + 1) % allGroupKeys.length
        : (currentIdx - 1 + allGroupKeys.length) % allGroupKeys.length;
      const nextGroup = allGroupKeys[nextIdx];
      setSelectedGroup(nextGroup);
      if (nextGroup !== "one_on_one") setOneOnOneStudent("");
    });
  };

  // Build a display label for a Master-Schedule highlight/small-group entry:
  // type tag + student first names (never the MS subject).
  const msGroupLabel = (g) => {
    const pool = signupPool.length ? signupPool : students;
    const names = (g.student_ids || []).map((id) => {
      const s = pool.find((p) => p.id === id);
      return s ? firstName(s) : null;
    }).filter(Boolean).sort((a, b) => a.localeCompare(b));
    const tag = (g.student_ids || []).length === 1 ? "1:1" : "Small group";
    return { tag, names: names.join(", ") };
  };

  const currentGroupLabel = selectedGroup.startsWith("custom_")
    ? customGroups.find(g => g.id === selectedGroup.replace("custom_", ""))?.name || "Custom"
    : selectedGroup.startsWith("signup_") ? (selectedSignup?.title || "Sign-Up")
    : selectedGroup.startsWith("msg_") ? (selectedMsGroup ? `${msGroupLabel(selectedMsGroup).tag} · ${msGroupLabel(selectedMsGroup).names}` : "Small group")
    : selectedGroup === "individual" ? "Whole school"
    : selectedGroup === "one_on_one" ? "1:1"
    : standardGroupTabs.find(t => t.value === selectedGroup)?.label || selectedGroup;

  // ---- Wizard navigation ----
  const pickBlock = (value) => {
    setSelectedBlock(value);
    if (value !== FULL_DAY) setStep("class");
  };
  const pickGroup = (value) => {
    setSelectedGroup(value);
    if (value !== "one_on_one") { setOneOnOneStudent(""); setStep("score"); }
  };
  const shiftDate = (delta) => {
    const d = new Date(selectedDate + "T00:00:00");
    d.setDate(d.getDate() + delta);
    setSelectedDate(toLocalDateStr(d));
  };

  const classLabel = className === "Other" ? resolvedOtherClass : className;
  const prettyDate = new Date(selectedDate + "T00:00:00").toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

  const stepSubtitle =
    step === "block" ? "Step 1 of 4"
    : step === "class" ? `Step 2 of 4 · ${selectedBlock}`
    : step === "group" ? `Step 3 of 4 · ${isFullDay ? TRIP_LABEL : `${selectedBlock} · ${classLabel}`}`
    : isFullDay ? `${TRIP_LABEL} · ${selectedTrip ? `${selectedTrip.title} (${tripBlockLabel})` : TRIP_LABEL}`
    : `${currentGroupLabel} · ${selectedBlock} · ${classLabel}`;

  const onBack =
    step === "class" ? () => setStep("block")
    : step === "group" ? () => setStep("class")
    : step === "score" ? () => setStep(isFullDay ? "block" : "group")
    : null;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="assessment-page">
      {/* Review DNA Dialog */}
      <DnaReviewDialog open={dnaReviewOpen} onOpenChange={setDnaReviewOpen} date={selectedDate} onSaved={fetchData} />

      {/* Custom Group Dialog */}
      <CustomGroupDialog
        open={groupDialogOpen}
        onOpenChange={(open) => { setGroupDialogOpen(open); if (!open) setEditingGroup(null); }}
        students={students} onSave={editingGroup ? handleUpdateGroup : handleCreateGroup} editingGroup={editingGroup}
      />

      {/* Wizard card */}
      <Card className="overflow-hidden">
        {/* Navy header */}
        <div className="bg-[#00305E] text-white px-4 sm:px-6 py-4 flex items-center gap-3">
          {onBack && (
            <button onClick={onBack} data-testid="wizard-back-btn"
              className="w-9 h-9 rounded-full bg-white/15 hover:bg-white/25 flex items-center justify-center shrink-0 transition-colors">
              <ChevronLeft />
            </button>
          )}
          <div className="min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight">Assessments</h1>
            <p className="text-sm text-white/70 truncate">{stepSubtitle}</p>
          </div>
          <div className="ml-auto flex items-center gap-2 shrink-0">
            <Button variant="outline" size="sm" onClick={() => setDnaReviewOpen(true)}
              className="bg-transparent border-white/30 text-white hover:bg-white/15 hover:text-white h-8" data-testid="dna-review-btn">
              Review DNA
            </Button>
            <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)}
              className="w-auto h-8 bg-white/10 border-white/30 text-white text-sm [color-scheme:dark]" data-testid="assessment-date-picker" />
          </div>
        </div>

        {/* Date nav bar */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-2.5 border-b bg-white">
          <button onClick={() => shiftDate(-1)} data-testid="assessment-prev-day"
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-[#00305E] transition-colors">
            <ChevronLeft size={18} />
          </button>
          <span className="font-bold text-slate-800">{prettyDate}</span>
          <button onClick={() => shiftDate(1)} data-testid="assessment-next-day"
            className="w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-[#00305E] transition-colors">
            <ChevronRight size={18} />
          </button>
        </div>

        <CardContent className="p-4 sm:p-6">
          {/* STEP 1 — BLOCK */}
          {step === "block" && (
            <div>
              <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-5">What block is this for?</h2>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {BLOCKS.map((b) => (
                  <button key={b} data-testid={`wiz-block-${b}`} onClick={() => pickBlock(b)}
                    className={`aspect-[3/2] sm:aspect-square rounded-xl border bg-white shadow-sm flex flex-col items-center justify-center hover:border-[#00305E] hover:shadow transition-all ${
                      selectedBlock === b && !isFullDay ? "border-[#00305E] ring-2 ring-[#00305E]/20" : "border-gray-200"
                    }`}>
                    <span className="text-3xl font-extrabold text-[#00305E]">{b.replace("Block ", "")}</span>
                    <span className="text-xs font-semibold text-slate-500 mt-1">Block</span>
                  </button>
                ))}
              </div>

              <button data-testid="wiz-block-full-day" onClick={() => pickBlock(FULL_DAY)}
                className={`mt-4 w-full h-14 rounded-xl border-2 flex items-center justify-center gap-2 font-bold transition-all ${
                  isFullDay ? "bg-orange-500 border-orange-500 text-white" : "bg-orange-50 border-orange-300 text-orange-600 hover:bg-orange-100"
                }`}>
                <SunIcon size={18} /> {TRIP_LABEL}
              </button>

              {isFullDay && (
                <div className="mt-6">
                  {fullDayTrips.length > 0 ? (
                    <>
                      <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Trip</Label>
                      <Select value={selectedTripId} onValueChange={setSelectedTripId}>
                        <SelectTrigger className="h-11 mt-1.5 border-amber-400 bg-amber-50" data-testid="full-day-trip-select">
                          <SelectValue placeholder="Select trip..." />
                        </SelectTrigger>
                        <SelectContent>
                          {fullDayTrips.map(t => <SelectItem key={t.id} value={t.id}>{t.title || "Trip"}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {selectedTrip && (
                        <p className="mt-2 text-xs text-amber-700" data-testid="trip-covered-blocks">
                          Covers: {tripBlockLabel}
                        </p>
                      )}
                      <Button onClick={() => setStep("score")} disabled={!selectedTripId}
                        data-testid="wiz-fullday-continue" className="mt-4 w-full h-11 bg-[#00305E] hover:bg-[#00305E]/90">
                        Continue <ChevronRight size={18} className="ml-1" />
                      </Button>
                    </>
                  ) : (
                    <p className="text-orange-600 text-sm" data-testid="full-day-no-trip">No trip scheduled for this day. Add one on the Trips tab.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* STEP 2 — CLASS */}
          {step === "class" && (
            <div>
              <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-2">What class/subject is this for?</h2>
              <p className="text-sm text-slate-500 mb-5">{selectedBlock} · applies to all students you assess next.</p>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Class / Subject</Label>
              <div className="mt-1.5">
                <Select value={className} onValueChange={(v) => { setClassName(v); if (v !== "Other") { setScheduleClassChoice(""); setOtherClassName(""); } else { setScheduleClassChoice(scheduleClasses.length ? "" : "__other__"); } }}>
                  <SelectTrigger className="w-full h-11" data-testid="assessment-class-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {subjectOptions.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {className === "Other" && (
                <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
                  <Label className="text-xs font-semibold uppercase tracking-wider text-amber-700">Pick the class from today's schedule</Label>
                  <p className="text-xs text-amber-600 mt-0.5 mb-2">Choose a scheduled class so names stay consistent. 1:1s aren't listed.</p>
                  <Select value={scheduleClassChoice} onValueChange={(v) => { setScheduleClassChoice(v); if (v !== "__other__") setOtherClassName(""); }}>
                    <SelectTrigger className="w-full h-11 bg-white" data-testid="assessment-schedule-class-select">
                      <SelectValue placeholder={scheduleClasses.length ? "Select a scheduled class..." : "No classes scheduled — choose Other"} />
                    </SelectTrigger>
                    <SelectContent>
                      {scheduleClasses.map(s => <SelectItem key={s} value={s} data-testid={`assessment-schedule-class-${s}`}>{s}</SelectItem>)}
                      <SelectItem value="__other__" data-testid="assessment-schedule-class-other">Other (type it in)</SelectItem>
                    </SelectContent>
                  </Select>
                  {scheduleClassChoice === "__other__" && (
                    <Input value={otherClassName} onChange={(e) => setOtherClassName(e.target.value)}
                      placeholder="Enter class name..." className="w-full h-11 mt-2 bg-white" data-testid="assessment-other-class-input" />
                  )}
                </div>
              )}
              <Button onClick={() => setStep("group")} disabled={!className || (className === "Other" && (!scheduleClassChoice || (scheduleClassChoice === "__other__" && !otherClassName.trim())))}
                data-testid="wiz-class-continue" className="mt-6 w-full h-11 bg-[#00305E] hover:bg-[#00305E]/90">
                Continue <ChevronRight size={18} className="ml-1" />
              </Button>
            </div>
          )}

          {/* STEP 2 — GROUP */}
          {step === "group" && (
            <div>
              <h2 className="text-2xl font-extrabold text-slate-900 tracking-tight mb-5">Who is this for?</h2>

              <button data-testid="group-tab-individual" onClick={() => pickGroup("individual")}
                className="w-full flex items-center gap-3 rounded-xl bg-[#00305E] text-white p-4 mb-3 hover:bg-[#00305E]/90 transition-colors">
                <div className="w-10 h-10 rounded-lg bg-white/15 flex items-center justify-center shrink-0"><UsersIcon /></div>
                <div className="text-left flex-1 min-w-0">
                  <p className="font-bold">Show me the whole-school list</p>
                  <p className="text-sm text-white/70">All present students</p>
                </div>
                <ChevronRight />
              </button>

              {/* Sign-Ups tied to this block on this date */}
              {blockSignups.length > 0 && (
                <div className="mb-3" data-testid="signup-groups-section">
                  <p className="text-xs font-semibold uppercase tracking-wider text-amber-600 mb-2">
                    Sign-Ups · {selectedBlock}
                  </p>
                  {blockSignups.map((o) => {
                    const count = getStudentsByGroup(`signup_${o.id}`).length;
                    return (
                      <button key={o.id} data-testid={`group-tab-signup_${o.id}`} onClick={() => pickGroup(`signup_${o.id}`)}
                        className="w-full flex items-center gap-3 rounded-xl bg-amber-50 border border-amber-300 p-4 mb-2.5 shadow-sm hover:border-amber-500 transition-colors">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                          <Icon size={18} d={<><path d="M9 11l3 3L22 4" /><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" /></>} />
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          <p className="font-bold text-slate-900 truncate">{o.title || "Sign-Up"}</p>
                          <p className="text-sm text-amber-700">Sign-up roster · {count} present of {o.student_count} signed up</p>
                        </div>
                        <ChevronRight className="text-amber-400" />
                      </button>
                    );
                  })}
                </div>
              )}

              {/* Master-Schedule 1:1 / small-group (Highlight + Individuals) for this block */}
              {blockHighlightGroups.length > 0 && (
                <div className="mb-3" data-testid="ms-highlight-groups-section">
                  <p className="text-xs font-semibold uppercase tracking-wider text-teal-600 mb-2">
                    From the Master Schedule · {selectedBlock}
                  </p>
                  {blockHighlightGroups.map((g) => {
                    const { tag, names } = msGroupLabel(g);
                    const count = getStudentsByGroup(`msg_${g.gid}`).length;
                    return (
                      <button key={g.gid} data-testid={`group-tab-msg_${g.gid}`} onClick={() => pickGroup(`msg_${g.gid}`)}
                        className="w-full flex items-center gap-3 rounded-xl bg-teal-50 border border-teal-300 p-4 mb-2.5 shadow-sm hover:border-teal-500 transition-colors">
                        <div className="w-10 h-10 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center shrink-0">
                          <UserIcon />
                        </div>
                        <div className="text-left flex-1 min-w-0">
                          <p className="font-bold text-slate-900 truncate">{tag}{names ? ` · ${names}` : ""}</p>
                          <p className="text-sm text-teal-700">Pull-out roster · {count} present of {g.student_count} scheduled</p>
                        </div>
                        <ChevronRight className="text-teal-400" />
                      </button>
                    );
                  })}
                </div>
              )}


              {groupOptions.length === 0 ? (
                <p className="text-sm text-slate-500 text-center py-4">No groups configured. Use the whole-school list above.</p>
              ) : groupOptions.map((g) => {
                const count = getStudentsByGroup(g.value).length;
                return (
                  <button key={g.value} data-testid={`group-tab-${g.value}`} onClick={() => pickGroup(g.value)}
                    className="w-full flex items-center gap-3 rounded-xl bg-white border border-gray-200 p-4 mb-2.5 shadow-sm hover:border-[#00305E] transition-colors">
                    <div className="w-10 h-10 rounded-lg bg-[#E8EEF5] text-[#00305E] flex items-center justify-center shrink-0"><GridIcon /></div>
                    <div className="text-left flex-1 min-w-0">
                      <p className="font-bold text-slate-900">{g.label}</p>
                      <p className="text-sm text-slate-500">{g.sub} · {count} student{count !== 1 ? "s" : ""}</p>
                    </div>
                    <ChevronRight className="text-slate-400" />
                  </button>
                );
              })}

              {customGroups.length > 0 && (
                <div className="mt-4 pt-4 border-t">
                  <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2">Custom Groups</p>
                  {customGroups.map((group) => (
                    <div key={group.id} className="flex items-center gap-2 mb-2.5">
                      <button data-testid={`group-tab-custom_${group.id}`} onClick={() => pickGroup(`custom_${group.id}`)}
                        className="flex-1 flex items-center gap-3 rounded-xl bg-white border border-gray-200 p-4 shadow-sm hover:border-[#00305E] transition-colors text-left">
                        <div className="w-10 h-10 rounded-lg bg-[#E8EEF5] text-[#00305E] flex items-center justify-center shrink-0"><GridIcon /></div>
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-slate-900">{group.name}</p>
                          <p className="text-sm text-slate-500">Custom group · {getStudentsByGroup(`custom_${group.id}`).length} student(s)</p>
                        </div>
                      </button>
                      <button onClick={() => { setEditingGroup(group); setGroupDialogOpen(true); }}
                        className="p-2 text-gray-400 hover:text-gray-600" title="Edit group">
                        <Icon size={16} d={<path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />} />
                      </button>
                      <button onClick={() => handleDeleteGroup(group.id)}
                        className="p-2 text-gray-400 hover:text-red-600" title="Delete group">
                        <Icon size={16} d={<><path d="M3 6h18" /><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6" /><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2" /></>} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* 1:1 single student */}
              <div className="mt-4 pt-4 border-t">
                <div className="rounded-xl bg-white border border-gray-200 p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#E8EEF5] text-[#00305E] flex items-center justify-center shrink-0"><UserIcon /></div>
                    <div className="flex-1 min-w-0">
                      <p className="font-bold text-slate-900">1:1 — single student</p>
                      <p className="text-sm text-slate-500">Assess one student individually</p>
                    </div>
                  </div>
                  <Select value={oneOnOneStudent}
                    onValueChange={(v) => { setSelectedGroup("one_on_one"); setOneOnOneStudent(v); setStep("score"); }}>
                    <SelectTrigger className="mt-3 h-11" data-testid="one-on-one-student-select">
                      <SelectValue placeholder="Choose a student..." />
                    </SelectTrigger>
                    <SelectContent>
                      {allStudentsSorted.map(s => <SelectItem key={s.id} value={s.id}>{firstName(s)}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button variant="outline" size="sm" className="w-full mt-3"
                  onClick={() => { setEditingGroup(null); setGroupDialogOpen(true); }}>
                  + Custom Group
                </Button>
              </div>
            </div>
          )}

          {/* STEP 3 — SCORE */}
          {step === "score" && (
            <div>
              {rubricImage && (
                <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 flex justify-center overflow-hidden" data-testid="assessment-rubric-image">
                  <img src={rubricImage} alt="Assessment rubric (1-4 scale reference)" className="max-h-72 w-auto object-contain" />
                </div>
              )}
              {/* Toolbar */}
              <div className="flex flex-wrap items-center gap-2 mb-4">
                <p className="text-sm text-slate-500 mr-auto" data-testid="score-summary-line">
                  {groupStudents.length} student{groupStudents.length !== 1 ? "s" : ""}
                  {isFullDay
                    ? ` · ${selectedTrip ? selectedTrip.title : "no trip"}`
                    : ` · ${presentStudentIds ? "present only" : "no attendance yet"}`}
                  {!isFullDay && blockUnscheduledIds && blockUnscheduledIds.size > 0 && (
                    <span className="text-amber-600" data-testid="block-unscheduled-note">
                      {` · ${blockUnscheduledIds.size} hidden (off-site / not in building this block)`}
                    </span>
                  )}
                </p>
                {!isFullDay && selectedGroup !== "one_on_one" && !isSignup && !isMsGroup && (
                  <div className="flex items-center gap-1">
                    <button onClick={() => handleQuickSwap("prev")} data-testid="quick-swap-prev"
                      className="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-gray-100 text-[#00305E] text-xs font-medium">
                      <ChevronLeft size={14} /> Prev Group
                    </button>
                    <button onClick={() => handleQuickSwap("next")} data-testid="quick-swap-next"
                      className="flex items-center gap-1 px-2 py-1.5 rounded-md hover:bg-gray-100 text-[#00305E] text-xs font-medium">
                      Next Group <ChevronRight size={14} />
                    </button>
                  </div>
                )}
                {!isFullDay && (
                  <Select value="" onValueChange={async (sourceBlock) => {
                    if (!sourceBlock) return;
                    try {
                      const res = await getAssessments({ date: selectedDate, block: sourceBlock });
                      const sourceData = res.data;
                      const updatedAssessments = { ...studentAssessments };
                      let copiedCount = 0;
                      groupStudents.forEach(student => {
                        const source = sourceData.find(a => a.student_id === student.id);
                        if (source) {
                          updatedAssessments[student.id] = {
                            did_not_attend: source.did_not_attend || false,
                            participation_level: source.participation_level,
                            engagement: source.engagement, communication: source.communication,
                            behavior: source.behavior, attention: source.attention,
                            work_completion: source.work_completion,
                            notes: source.notes || "", alternate_plan: source.alternate_plan || "",
                          };
                          copiedCount++;
                        }
                      });
                      setStudentAssessments(updatedAssessments);
                      setHasUnsavedChanges(true);
                      toast.success(`Copied scores for ${copiedCount} student(s) from ${sourceBlock}`);
                    } catch { toast.error("Failed to copy scores"); }
                  }}>
                    <SelectTrigger className="w-[130px] h-8 text-sm text-muted-foreground" data-testid="copy-from-block">
                      <SelectValue placeholder="Copy from..." />
                    </SelectTrigger>
                    <SelectContent>
                      {BLOCKS.filter(b => b !== selectedBlock).map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                    </SelectContent>
                  </Select>
                )}
              </div>

              {groupStudents.length > 0 ? (
                <div className="space-y-3">
                  {groupStudents.map((student) => (
                    <StudentAssessmentCard key={student.id} student={student}
                      assessment={studentAssessments[student.id] || {
                        did_not_attend: false, participation_level: null, engagement: null,
                        communication: null, behavior: null, attention: null, work_completion: null,
                        notes: "", alternate_plan: "",
                      }}
                      onChange={(assessment) => handleStudentAssessmentChange(student.id, assessment)} />
                  ))}
                  <Button onClick={handleSubmitAll} disabled={submitting}
                    data-testid="submit-all-assessments-btn"
                    className="w-full mt-4 h-12 bg-[#00305E] hover:bg-[#00305E]/90 text-base font-bold">
                    <Icon size={18} className="mr-2" d={<><path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" /><path d="M17 21v-8H7v8M7 3v5h8" /></>} />
                    {submitting ? "Saving..." : hasUnsavedChanges ? "Save Assessments *" : "Save Assessments"}
                  </Button>
                </div>
              ) : (
                <div className="text-center py-12 text-muted-foreground" data-testid="score-empty-state">
                  <p className="text-lg mb-2">{isFullDay ? "No students on this trip" : "No students to assess"}</p>
                  <p className="text-sm mb-4">
                    {isFullDay
                      ? "Pick a trip with sign-ups, or add students on the Trips tab."
                      : presentStudentIds ? "Check that students are marked present, or pick a different group." : "No students in this group."}
                  </p>
                  <Button variant="outline" onClick={() => setStep(isFullDay ? "block" : "group")}>
                    <ChevronLeft size={16} className="mr-1" /> {isFullDay ? "Change trip" : "Change group"}
                  </Button>
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Today's Assessment Entries */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
            <div>
              <CardTitle className="text-lg">Assessment Entries for {selectedDate}</CardTitle>
              <CardDescription>All assessments entered for this date. Click a row to edit.</CardDescription>
            </div>
            <Button size="sm" variant="outline" className="text-orange-700 border-orange-300 hover:bg-orange-50 self-start sm:self-auto whitespace-nowrap"
              data-testid="review-dna-entries-btn"
              onClick={() => setDnaReviewOpen(true)}>
              Review DNA
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {assessments.length === 0 ? (
            <p className="text-center py-8 text-muted-foreground">No assessments entered yet for this date</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#0d2240] text-white">
                    <th className="px-3 py-2 text-left font-semibold">Student</th>
                    <th className="px-2 py-2 text-left font-semibold">Class</th>
                    <th className="px-2 py-2 text-left font-semibold">Block</th>
                    <th className="px-2 py-2 text-center font-semibold text-xs">Attend</th>
                    <th className="px-2 py-2 text-center font-semibold text-xs">Engage</th>
                    <th className="px-2 py-2 text-center font-semibold text-xs">Comm</th>
                    <th className="px-2 py-2 text-center font-semibold text-xs">Behav</th>
                    <th className="px-2 py-2 text-center font-semibold text-xs">Attn</th>
                    <th className="px-2 py-2 text-center font-semibold text-xs">Task</th>
                    <th className="px-2 py-2 text-center font-semibold text-xs">DNA</th>
                    <th className="px-2 py-2 text-left font-semibold">Notes</th>
                    <th className="px-2 py-2 text-center font-semibold">Edit</th>
                  </tr>
                </thead>
                <tbody>
                  {[...assessments]
                    .sort((a, b) => (a.class_name || "").localeCompare(b.class_name || "") || (a.student_id || "").localeCompare(b.student_id || ""))
                    .map((entry) => {
                      const studentObj = students.find(s => s.id === entry.student_id) || fullDayStudentPool.find(s => s.id === entry.student_id);
                      const sName = studentObj
                        ? `${studentObj.preferred_name || studentObj.first_name} ${studentObj.last_name}`
                        : entry.student_id;
                      return (
                        <AssessmentEntryRow key={entry.id} entry={entry} studentName={sName}
                          subjectOptions={subjectOptions}
                          onSave={async (id, updates) => {
                            try {
                              await updateAssessment(id, updates);
                              toast.success("Assessment updated");
                              fetchData();
                            } catch (e) { toast.error("Update failed"); }
                          }}
                          onDelete={async (id) => {
                            try {
                              await deleteAssessment(id);
                              toast.success("Assessment deleted");
                              fetchData();
                            } catch (e) { toast.error("Delete failed"); }
                          }} />
                      );
                    })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
